import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Reveal } from "@/components/shared/Reveal";
import { Trophy, Users, Share2, Rocket, Star, ArrowRight, Sparkles, Lock } from "lucide-react";
import { openPreRegister } from "@/lib/preRegisterModal";
import { PerksNotifyToggle } from "@/components/shared/PerksNotifyToggle";
import { usePerksNotifyCount } from "@/hooks/usePerksNotifyCount";
import { LaunchCountdown } from "@/components/shared/LaunchCountdown";

const STEPS = [
  { icon: Share2, title: "Pre-register & get your link", desc: "Every pre-registration comes with a unique, shareable referral link." },
  { icon: Users, title: "Invite drivers, carriers & RV owners", desc: "Share your link — anyone who pre-registers through it counts toward your total." },
  { icon: Trophy, title: "Hit 5+ referrals", desc: "Reach 5 successful referrals and you unlock Founding Ambassador status." },
];

const PERKS = [
  { icon: Star, title: "Public Recognition", desc: "Founding Ambassadors are featured on the public Top Referrers leaderboard with a badge next to their name." },
  { icon: Rocket, title: "Priority at Launch", desc: "Ambassadors are prioritized when we activate their region as RelayRigs launches." },
  { icon: Trophy, title: "Exclusive Founding Perks", desc: "We're actively designing extra founding-ambassador perks for launch — stay on the list to be first to hear about them." },
];

export default function AmbassadorProgram() {
  const perksNotifyCount = usePerksNotifyCount();
  return (
    <div data-testid="ambassador-program-page" className="min-h-screen bg-white">
      <Header />

      <section id="ambassador-hero" data-testid="ambassador-hero-section" className="pt-36 pb-20 bg-rr-navy relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-rr-orangeLight text-xs font-bold tracking-[0.2em] uppercase mb-5">
              <Trophy size={14} /> Founding Ambassador Program
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Help Build the RelayRigs Network, Get Recognized For It
            </h1>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Founding Ambassadors are pre-registered drivers, carriers, and RV owners who bring 5 or more people into the RelayRigs
              network before launch. It's our way of recognizing the people helping RelayRigs grow fastest.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="ambassador-how" data-testid="ambassador-how-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orange mb-4">How It Works</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy tracking-tight">Three Steps to Ambassador Status</h2>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, idx) => (
              <div key={s.title} data-testid={`ambassador-step-${idx}`} className="border border-rr-line rounded-2xl p-6">
                <div className="w-11 h-11 rounded-full bg-rr-surface flex items-center justify-center text-rr-navy mb-4">
                  <s.icon size={19} />
                </div>
                <h3 className="font-heading font-bold text-rr-navy">{s.title}</h3>
                <p className="text-sm text-rr-muted mt-1.5 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="ambassador-perks" data-testid="ambassador-perks-section" className="py-20 md:py-28 bg-rr-surface">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orange mb-4">What You Get</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy tracking-tight">Founding Ambassador Perks</h2>
          </Reveal>

          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {PERKS.map((p, idx) => (
              <div key={p.title} data-testid={`ambassador-perk-${idx}`} className="bg-white border border-rr-line rounded-2xl p-6">
                <div className="w-11 h-11 rounded-full bg-rr-navy flex items-center justify-center text-white mb-4">
                  <p.icon size={19} />
                </div>
                <h3 className="font-heading font-bold text-rr-navy">{p.title}</h3>
                <p className="text-sm text-rr-muted mt-1.5 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div
              data-testid="ambassador-perks-teaser"
              className="mt-6 relative bg-rr-navy rounded-2xl p-7 md:p-8 text-center overflow-hidden"
            >
              <div className="absolute inset-0 grain-overlay opacity-40" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-rr-orangeLight text-xs font-bold tracking-[0.2em] uppercase mb-4">
                  <Lock size={13} /> Launching January 1, 2027
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-extrabold text-white flex items-center justify-center gap-2">
                  <Sparkles size={20} className="text-rr-orangeLight" /> Exclusive Founding Ambassador Perks
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/70 max-w-lg mx-auto leading-relaxed">
                  We're finalizing extra rewards reserved just for our top Founding Ambassadors. Stay pre-registered and keep
                  referring — you'll be the first to know the moment they're revealed.
                </p>
                <div className="mt-5">
                  <LaunchCountdown />
                </div>
                {perksNotifyCount !== null && perksNotifyCount > 0 && (
                  <p data-testid="perks-notify-count" className="mt-5 text-xs font-bold uppercase tracking-wide text-rr-orangeLight">
                    {perksNotifyCount.toLocaleString()} {perksNotifyCount === 1 ? "person" : "people"} notified so far
                  </p>
                )}
                <PerksNotifyToggle />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="ambassador-cta" data-testid="ambassador-cta-section" className="py-20 md:py-28 bg-white text-center">
        <div className="max-w-2xl mx-auto px-5 md:px-8">
          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy tracking-tight">Ready to Start Referring?</h2>
            <p className="mt-4 text-rr-muted text-sm md:text-base leading-relaxed">
              Pre-register to get your own referral link, or check the leaderboard to see this week's top Founding Ambassadors.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => openPreRegister("driver")}
                data-testid="ambassador-cta-preregister-button"
                className="inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-4 rounded-full hover:bg-rr-navy transition-all hover:-translate-y-1"
              >
                Pre-Register for RelayRigs <ArrowRight size={18} />
              </button>
              <a
                href="/#top-referrers"
                data-testid="ambassador-cta-leaderboard-link"
                className="inline-flex items-center gap-1 text-rr-navy font-bold underline underline-offset-4 decoration-rr-navy/30 hover:decoration-rr-navy px-2"
              >
                View the Leaderboard →
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
